import useSWRInfinite from 'swr/infinite';

// logic
import { SETTINGS, STATES } from '../../constants';
// api
import getTicketsAPICall from '../../api/getTickets';
import { useState } from 'react';
import moment from 'moment';

// calls api to get tickets
const ticketsFetcher = async (...args) => {
  return getTicketsAPICall(...args);
};

const getNotCancelledTickets = () => {
  const query = `char={"name": "synthetic_state", "op": "iregex", "value": "^(?!CANCELLED)"}`;
  return query;
};

const getSearchQueryFromFilters = (filters) => {
  try {
    const { search } = filters;
    const isSearchValid = search.trim() !== '' && search.length > 0;
    if (!isSearchValid) return null;
    const query = `q={"name": "search", "op": "raw", "value": "'${search}':*"}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getCreatedAtAfterFromFilters = (filters) => {
  try {
    const { created_at_after } = filters;
    const momentDate = moment(created_at_after);
    const isCreatedAtAfterValid = momentDate.isValid();
    if (!isCreatedAtAfterValid) return null;
    const dateFormated = momentDate.format('YYYY-MM-DDTHH:mm:ss');
    const query = `created_at_after=${dateFormated}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getStateQueryFromFilters = (filters) => {
  try {
    const { state } = filters;
    const isStateValid = Object.keys(STATES)
      .map((key) => STATES[key])
      .includes(state);
    if (!isStateValid) return null;
    const query = `char={"name": "synthetic_state", "value": "${state}"}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getAssignedToQueryFromFilters = (filters) => {
  try {
    const { assigned_to } = filters;
    const isAssignedToValid = assigned_to > 0;
    if (!isAssignedToValid) return null;
    const query = `assigned_to=${assigned_to}`;
    return query;
  } catch (e) {
    return null;
  }
};

const getPageSizeQueryFromSettings = () => {
  const page_size = SETTINGS.TICKETS_LIST_PAGE_SIZE;
  const query = `page_size=${page_size}`;
  return query;
};

const getTicketsQueryFromFilters = (filters) => {
  let query = ``;
  const pageSizeQuery = getPageSizeQueryFromSettings();
  const searchQuery = getSearchQueryFromFilters(filters);
  const createdAtAfterQuery = getCreatedAtAfterFromFilters(filters);
  const stateQuery = getStateQueryFromFilters(filters);
  const assignedToQuery = getAssignedToQueryFromFilters(filters);
  const notCancelledTickets = getNotCancelledTickets();
  const queries = [
    pageSizeQuery,
    notCancelledTickets,
    searchQuery,
    createdAtAfterQuery,
    stateQuery,
    assignedToQuery,
  ];
  queries.forEach((currentQuery, index) => {
    if (!!currentQuery) {
      const paramSeparator = index === 0 ? '?' : '&';
      query = `${query}${paramSeparator}${currentQuery}`;
    }
  });
  return query;
};

// returns tickets with loading/reloading state
const useTicketsList = (props) => {
  const { selectedFilters } = props;
  const [lastUpdate, setLastUpdate] = useState(null);

  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.length) return null; // reached the end
    const query = `${getTicketsQueryFromFilters(selectedFilters)}&page=${
      pageIndex + 1
    }`;
    return `tickets/${query}`;
  };

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const {
    data: ungroupedTickets,
    error: ticketsError,
    isValidating: isReloadingTickets,
    size,
    setSize,
    mutate,
  } = useSWRInfinite(getKey, ticketsFetcher, {
    refreshInterval: SETTINGS.RELOAD_TICKETS_LIST_INTERVAL_IN_SECONDS * 1000,
    revalidateAll: true,
    revalidateIfStale: true,
    onSuccess: () => {
      const nowTime = moment().format('HH:mm:ss');
      setLastUpdate(nowTime);
      setIsLoadingMore(false);
    },
  });

  const isEmpty = ungroupedTickets?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty ||
    (ungroupedTickets &&
      ungroupedTickets[ungroupedTickets.length - 1]?.length <
        SETTINGS.TICKETS_LIST_PAGE_SIZE);

  const isLoadingTickets = typeof ungroupedTickets === 'undefined';

  const loadMore = () => {
    setIsLoadingMore(true);
    return setSize(size + 1);
  };

  const reset = () => {
    mutate();
  };

  return {
    ungroupedTickets,
    isEmpty,
    isReachingEnd,
    isLoadingMore,
    lastUpdate,
    // first load
    isLoadingTickets,
    // reloads
    isReloadingTickets,
    ticketsError,
    loadMore,
    reset,
  };
};

export default useTicketsList;
